/**
 * Design tokens for the customers book.
 *
 * The same palette the mobile app uses — `lib/view/components/shared/app_ui.dart`
 * carries these exact values (kCanvas #F4F5F7, kInk #15181D, kInkSoft #6B7280,
 * kInkFaint #9AA1AC), so the two clients already agree and nothing had to be
 * translated. Restated here rather than imported from another feature's theme,
 * the way the orders and login screens each own theirs.
 */
export const T = {
  canvas: '#F4F5F7',
  surface: '#FFFFFF',
  sunk: '#FAFAFB',
  shade: '#F8F8F8',
  hover: '#F5F6F8',
  border: '#E6E8EC',
  borderStrong: '#D8DCE2',
  hairline: '#F0F1F4',

  ink: '#15181D',
  inkSoft: '#6B7280',
  inkFaint: '#9AA1AC',
  inkGhost: '#C2C7CF',

  brand: '#F64829',
  brandInk: '#C3341A',
  brandTint: '#FFEBE6',
  selectedBg: '#FFF1ED',
  selectedBorder: '#F7C6B9',

  paid: '#16A34A',
  paidTint: '#E9F7EF',
  pendingInk: '#B87409',
  pendingTint: '#FDF3E2',
  pendingBorder: '#E3C892',

  danger: '#DC2626',
  dangerTint: '#FDECEC',
  dangerBorder: '#F0B0AB',

  radiusPanel: 10,
  radiusControl: 8,
  radiusDialog: 12,
  radiusPill: 999,
  listWidth: 400,

  dialog: '0 12px 32px rgba(21,24,29,0.10)',
  backdrop: 'rgba(21,24,29,0.44)',
};

export const FONT_STACK =
  '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

/** Money and dates line up in columns, so every figure uses lining figures. */
export const NUMERIC = { fontVariantNumeric: 'tabular-nums' };

/**
 * The balance colour, copied exactly from the mobile app's customer_model.dart:
 *
 *     Color get color => balance < 0 ? Colors.red : primaryColor;
 *
 * Which reads backwards to most people: a customer who OWES you shows in the
 * brand orange, and one you owe shows red. Zero is orange too. Reproduced
 * rather than corrected, so the phone and the till never disagree about the
 * same customer — if it is wrong, it is wrong in one place and both change.
 */
export function balanceColor(balance) {
  return Number(balance) < 0 ? T.danger : T.brand;
}

/** `$182.00`, or `−$12.00` when the shop is the one in debt. */
export function money(amount, mark = '$') {
  const value = Number(amount || 0);
  const body = `${mark}${Math.abs(value).toFixed(2)}`;
  return value < 0 ? `−${body}` : body;
}
